import type {
  ForwardRefExoticComponent,
  ReactElement,
  RefAttributes,
} from 'react';
import { Children, cloneElement, forwardRef, useReducer, useRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

import { IconToggle } from './IconToggle';
import { Toggle } from './Toggle';
import { shouldForwardProp } from '@utils/styledProps';
import { reducer, initState } from './toggle-group-reducer';
import type {
  IconToggleOrToggle,
  IconToggleElement,
} from './toggle-group-models';
import { ToggleGroupProvider } from './toggle-group-context';

interface ToggleGroupCommonProps {
  /**
   * Accessible group description visible only to screen readers.
   */
  description: string;
  /**
   * Callback on selection change.
   */
  onChange: (value: string) => void;
  /**
   * Selected value of the group. Works as controlled component.
   */
  selectedValue: string;
  /**
   * Styles override.
   */
  className?: string;
}

type OrientationProps =
  | {
      /**
       * Toggle group orientation. Please note that a `ToggleGroup` with vertical orientation must contain
       * only `ToggleGroup.IconToggle`.
       */
      orientation?: 'horizontal';
      /**
       * List of `ToggleGroup.Toggle` or `ToggleGroup.IconToggle` components.
       */
      children: IconToggleOrToggle[];
    }
  | {
      orientation?: 'vertical';
      children: IconToggleElement[];
    };

export type ToggleGroupProps = ToggleGroupCommonProps & OrientationProps;

export interface CompoundedToggleGroup
  extends ForwardRefExoticComponent<
    ToggleGroupProps & RefAttributes<HTMLInputElement>
  > {
  IconToggle: typeof IconToggle;
  Toggle: typeof Toggle;
}

interface ContainerProps {
  isVertical: boolean;
}

const Container = styled('div', {
  shouldForwardProp: shouldForwardProp(['isVertical']),
})<ContainerProps>`
  display: inline-flex;
  flex-direction: ${({ isVertical }) => (isVertical ? 'column' : 'row')};
  gap: ${tokens.spacer.x0};
  padding: ${tokens.spacer.x0};
  background: ${tokens.color.neutral_200};
  border-radius: ${tokens.radius.l};
`;

/**
 * ToggleGroup component.
 *
 * {@link https://zeroheight.com/76c439891/p/57c441-button-toggle-group Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-togglegroup Code documentation}
 *
 * @example
 * <ToggleGroup description="List of Actions" selectedValue="1">
 *   <ToggleGroup.Toggle value="1">Action 1</ToggleGroup.Toggle>
 *   <ToggleGroup.IconToggle value="2" icon="..." tooltipText="Action 2" />
 * </ToggleGroup>
 */
// ESLint gets confused by the TS type casting that's required to expose the Toggle and IconToggle
// eslint-disable-next-line react/display-name
export const ToggleGroup = forwardRef<HTMLDivElement, ToggleGroupProps>(
  (
    {
      children,
      className,
      description,
      onChange,
      selectedValue,
      orientation,
    }: ToggleGroupProps,
    ref,
  ) => {
    const childrenRefs = useRef([]);

    function findNextAvailableToggle(
      focusedIndex: number,
      toggles: IconToggleOrToggle[],
      direction: 'next' | 'previous',
    ): number {
      let nextToggleIndex = focusedIndex;
      do {
        if (direction === 'previous') {
          nextToggleIndex =
            nextToggleIndex === 0 ? toggles.length - 1 : nextToggleIndex - 1;
        } else {
          nextToggleIndex =
            nextToggleIndex >= toggles.length - 1 ? 0 : nextToggleIndex + 1;
        }
        if (!toggles[nextToggleIndex].props.isDisabled) break;
      } while (focusedIndex !== nextToggleIndex);

      return nextToggleIndex;
    }

    const [state, dispatch] = useReducer(
      reducer,
      { children, selectedValue },
      initState,
    );

    const getIsPreviousKey = (event: KeyboardEvent): boolean => {
      if (orientation === 'vertical') {
        return event.key === 'ArrowUp';
      }
      return event.key === 'ArrowLeft';
    };

    const getIsNextKey = (event: KeyboardEvent): boolean => {
      if (orientation === 'vertical') {
        return event.key === 'ArrowDown';
      }
      return event.key === 'ArrowRight';
    };

    const mappedChildren = Children.map(
      children,
      (child: ReactElement, index: number) =>
        cloneElement(child, {
          ...child.props,
          tabIndex: state.focusedIndex === index ? '0' : '-1',
          ref: (childRef: ReactElement) => {
            childrenRefs.current[index] = childRef;
          },
          onKeyDown: (e) => {
            const isPreviousKey = getIsPreviousKey(e);
            const isNextKey = getIsNextKey(e);

            if (isPreviousKey || isNextKey) {
              // stop propagation of the event so the page is not scrolled in the direction pressed
              e.preventDefault();

              const newIndex = findNextAvailableToggle(
                state.focusedIndex,
                children,
                isPreviousKey ? 'previous' : 'next',
              );
              if (newIndex !== state.focusedIndex) {
                childrenRefs.current[newIndex].focus();
                dispatch({ type: 'focus', index: newIndex });
              }
            }
            if (child.props.onKeyDown) child.props.onKeyDown(e);
          },
          onClick: (e) => {
            if (child.props.isDisabled) return;
            dispatch({ type: 'select', index });
            if (onChange) onChange(child.props.value);
            if (child.props.onClick) child.props.onClick(e);
          },
        }),
    );

    return (
      <Container
        aria-label={description}
        className={className}
        ref={ref}
        role="radiogroup"
        isVertical={orientation === 'vertical'}
      >
        <ToggleGroupProvider
          orientation={orientation}
          selectedValue={selectedValue}
        >
          {mappedChildren}
        </ToggleGroupProvider>
      </Container>
    );
  },
) as CompoundedToggleGroup;

ToggleGroup.IconToggle = IconToggle;
ToggleGroup.Toggle = Toggle;
ToggleGroup.displayName = 'ToggleGroup';

ToggleGroup.defaultProps = {
  orientation: 'horizontal',
};
